import { useEffect, useMemo, useState } from "react";
import CloseRounded from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import { isApiError } from "@bw/shared/api";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import type {
  BookingParticipantResponse,
  PlayerCard as PlayerCardData,
} from "@bw/shared/types/generated";
import { usePlayerSearch } from "../hooks/usePlayerSearch";
import { useCreateBookingInvitation } from "../hooks/useCreateBookingInvitation";
import { PlayerPeekDialog } from "../../../app/components/PlayerPeekDialog";

/** Debounce the search box so each keystroke does not fire a request. Short
 *  enough that the ≥2-char search still resolves promptly. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Host-only "Invite a player" dialog — the single front-door for bringing someone
 * onto an already-paid court. It reuses the EXISTING player-search seam
 * (usePlayerSearch → socialApi.searchPlayers, a general by-NAME search) to find a
 * player, then picks one to INVITE to the court via useCreateBookingInvitation. An
 * invite does NOT add a roster row — it creates a PENDING invitation; the invitee
 * joins the roster once they accept. So:
 *   - Roster members + the host's own uid + anyone with an ALREADY-PENDING invite
 *     are EXCLUDED from the search results (re-inviting them would 409).
 *   - On a successful invite the searched row shows "Invited" and the dialog STAYS
 *     OPEN (a host may invite several in one sitting), and the pending-invitations
 *     list is invalidated so the exclusion set re-reads server truth.
 *   - A 409 duplicate → an honest "already invited" inline (role=alert), dialog
 *     stays open — never a fake success-close.
 *
 * MUI provides the WAI-ARIA modal, focus trap, Escape handling, and backdrop.
 *
 * THIN presentation layer: the server owns the search + the invite; the only
 * client logic is the exclusion set (a UX guard against a 409) and the
 * locally-tracked "Invited" cue for rows already invited this sitting.
 */
export function InviteParticipantDialog({
  facilityId,
  bookingId,
  participants = [],
  pendingInviteeIds = [],
  onClose,
}: {
  facilityId: string;
  bookingId: string;
  // The already-loaded roster (server truth) — excluded from the search so a
  // dupe can't be picked. Defaults to [] (roster still loading / empty).
  participants?: BookingParticipantResponse[];
  // The ids with an ALREADY-PENDING invite for this booking (server truth) —
  // also excluded so re-inviting them (a 409) can't be attempted.
  pendingInviteeIds?: string[];
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [peek, setPeek] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  // The user_id of the result currently being invited — drives the per-row
  // busy/disabled latch so only the clicked row reads "Inviting…", and a pending
  // invite on row A never freezes row B.
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);
  // The ids successfully invited DURING this sitting — their row flips to
  // "Invited" (the search results don't refetch, so the candidate stays visible;
  // we mark it locally as done). The server-truth exclusion arrives on the next
  // search via the invalidated pending list.
  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);

  // Everyone the host can't validly invite: themselves + everyone already on the
  // roster + everyone with a pending invite. Excluded from search results (the
  // invite would 409).
  const excludeUserIds = useMemo(() => {
    const ids = participants.map((p) => p.user_id);
    ids.push(...pendingInviteeIds);
    if (user?.uid) ids.push(user.uid);
    return ids;
  }, [participants, pendingInviteeIds, user?.uid]);

  const debounced = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  // The LOCAL booking usePlayerSearch bakes the ≥2-char `enabled` gate + the
  // blank-guard over socialApi.searchPlayers; scope is "all" (general by-name)
  // and the roster + self + pending invitees are filtered out of the results.
  const searchQ = usePlayerSearch(debounced, excludeUserIds);
  const invite = useCreateBookingInvitation(facilityId, bookingId);

  // Clear the per-row latch once an invite settles.
  useEffect(() => {
    if (!invite.isPending) setInvitingUserId(null);
  }, [invite.isPending]);

  function handleSelect(userId: string) {
    if (invite.isPending) return;
    setInvitingUserId(userId);
    // The CLICKED row's user_id rides this call (per-row scoped) — NOT the first
    // result's. The dialog STAYS OPEN on success (a host may invite several); the
    // invited row flips to "Invited". A rejection keeps the dialog open to surface
    // the honest error.
    invite.mutate(userId, {
      onSuccess: () => setInvitedUserIds((prev) => [...prev, userId]),
    });
  }

  const results: PlayerCardData[] = searchQ.data ?? [];
  const searchActive = debounced.trim().length >= 2;

  return (
    <Dialog
      open
      aria-labelledby="invite-participant-title"
      onClose={() => {
        if (!invite.isPending && !peek) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="invite-participant-title" component="div">
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
            Invite a player
          </Typography>
          <IconButton
            type="button"
            size="small"
            onClick={onClose}
            disabled={invite.isPending}
            aria-label="Close"
            title="Close"
          >
            <CloseRounded aria-hidden="true" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* This distinguishes an invitation from an immediate roster add. */}
          <Typography color="text.secondary" variant="body2">
            Invited players join once they accept.
          </Typography>

          <Stack
            component="form"
            role="search"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              autoFocus
              fullWidth
              type="text"
              placeholder="Search players by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                htmlInput: { "aria-label": "Search players by name" },
              }}
            />
          </Stack>

          {invite.isError ? (
            <Alert severity="error" role="alert">
              {inviteErrorMessage(invite.error)}
            </Alert>
          ) : null}

          {!searchActive ? (
            <Typography color="text.secondary">
              Search by name to invite a player to this court.
            </Typography>
          ) : searchQ.isError ? (
            <Alert severity="error" role="alert">
              We couldn&apos;t search right now. Please try again.
            </Alert>
          ) : searchQ.isPending || searchQ.isFetching ? (
            <Typography color="text.secondary" role="status" aria-live="polite">
              Searching…
            </Typography>
          ) : results.length === 0 ? (
            <Typography color="text.secondary">No players found.</Typography>
          ) : (
            <List disablePadding aria-label="Player search results">
              {results.map((player) => {
                const isInviting =
                  invite.isPending && invitingUserId === player.user_id;
                const isInvited = invitedUserIds.includes(player.user_id);
                return (
                  <ListItem key={player.user_id} disableGutters divider>
                    <MaterialPlayerCard
                      userId={player.user_id}
                      displayName={player.display_name}
                      avatarUrl={player.avatar_url}
                      rating={player.rating}
                      ratingDivision={player.rating_division}
                      relationship={player.relationship}
                      onClickIdentity={() =>
                        setPeek({
                          userId: player.user_id,
                          displayName: player.display_name,
                        })
                      }
                      actions={
                        <Button
                          type="button"
                          size="small"
                          variant="contained"
                          onClick={() => handleSelect(player.user_id)}
                          disabled={invite.isPending || isInvited}
                          aria-busy={isInviting || undefined}
                        >
                          {isInvited
                            ? "Invited"
                            : isInviting
                              ? "Inviting…"
                              : "Invite"}
                        </Button>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Stack>

        {peek ? (
          <PlayerPeekDialog
            userId={peek.userId}
            displayName={peek.displayName}
            onClose={() => setPeek(null)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Map an invite rejection to an honest, action-specific message (status-first
 *  so a renamed code can't blank the UI). */
function inviteErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 409) {
      return "That player has already been invited.";
    }
    if (error.status === 404) {
      return "We couldn't invite that player — they have no profile here.";
    }
    if (error.status === 403) {
      return "You're not allowed to invite players to this booking.";
    }
    return error.message || "We couldn't invite that player. Please try again.";
  }
  return "We couldn't invite that player. Please try again.";
}
